import React from "react";
import { Check } from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/context/LanguageContext";

type FeatureRow =
  | { type: "category"; label: string }
  | {
      type: "feature";
      feature: string;
      tooltip?: string;
      free: string | boolean;
      pro: string | boolean;
      team: string | boolean;
    };

function CellValue({ value }: { value: string | boolean }) {
  if (value === true) {
    return <Check className="mx-auto h-4 w-4 text-primary" aria-label="Included" />;
  }
  if (value === false) {
    return <span className="text-muted-foreground">—</span>;
  }
  return <>{value}</>;
}

function FeatureLabel({ feature, tooltip }: { feature: string; tooltip?: string }) {
  if (!tooltip) {
    return <span>{feature}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button className="cursor-help underline decoration-muted-foreground/50 decoration-dashed underline-offset-2">
          {feature}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export function PlansFeatureTable() {
  const { t } = useTranslation();

  const FEATURES: FeatureRow[] = [
    // ── Content ──
    { type: "category", label: t("limits.content") },
    {
      type: "feature",
      feature: t("limits.addingContent"),
      tooltip: t("limits.addingContentTooltip"),
      free: t("limits.free3day"),
      pro: t("limits.unlimited"),
      team: t("limits.unlimited"),
    },
    {
      type: "feature",
      feature: t("limits.docPageLimit"),
      free: t("limits.free100pages"),
      pro: t("limits.pro2000pages"),
      team: t("limits.team5000pages"),
    },
    {
      type: "feature",
      feature: t("limits.docFileSize"),
      free: t("limits.free10mb"),
      pro: t("limits.pro300mb"),
      team: t("limits.team500mb"),
    },

    // ── Chat ──
    { type: "category", label: t("limits.chat") },
    {
      type: "feature",
      feature: t("limits.aiChatMessages"),
      tooltip: t("limits.aiChatMessagesTooltip"),
      free: t("limits.free5day"),
      pro: t("limits.pro100day"),
      team: t("limits.pro100day"),
    },
    {
      type: "feature",
      feature: t("limits.aiModelAccess"),
      tooltip: t("limits.aiModelAccessTooltip"),
      free: t("limits.standardAuto"),
      pro: t("limits.advancedModels"),
      team: t("limits.advancedModels"),
    },

    // ── Study tools ──
    { type: "category", label: t("limits.studyTools") },
    {
      type: "feature",
      feature: t("limits.flashcards"),
      tooltip: t("limits.flashcardsTooltip"),
      free: t("limits.free10day"),
      pro: t("limits.unlimited"),
      team: t("limits.unlimited"),
    },
    {
      type: "feature",
      feature: t("limits.summaries"),
      tooltip: t("limits.summariesTooltip"),
      free: t("limits.free5day"),
      pro: t("limits.unlimited"),
      team: t("limits.unlimited"),
    },
    {
      type: "feature",
      feature: t("limits.quizAnswers"),
      tooltip: t("limits.quizAnswersTooltip"),
      free: t("limits.free10day"),
      pro: t("limits.unlimited"),
      team: t("limits.unlimited"),
    },
    {
      type: "feature",
      feature: t("limits.practiceExams"),
      tooltip: t("limits.practiceExamsTooltip"),
      free: t("limits.free2month"),
      pro: t("limits.unlimited"),
      team: t("limits.unlimited"),
    },
    {
      type: "feature",
      feature: t("limits.mindMaps"),
      tooltip: t("limits.mindMapsTooltip"),
      free: t("limits.free3dayVisual"),
      pro: t("limits.unlimited"),
      team: t("limits.unlimited"),
    },
    {
      type: "feature",
      feature: t("limits.flowCharts"),
      tooltip: t("limits.flowChartsTooltip"),
      free: t("limits.free3dayVisual"),
      pro: t("limits.unlimited"),
      team: t("limits.unlimited"),
    },
    {
      type: "feature",
      feature: t("limits.timelines"),
      tooltip: t("limits.timelinesTooltip"),
      free: t("limits.free3dayVisual"),
      pro: t("limits.unlimited"),
      team: t("limits.unlimited"),
    },
    {
      type: "feature",
      feature: t("limits.charts"),
      tooltip: t("limits.chartsTooltip"),
      free: t("limits.free3dayVisual"),
      pro: t("limits.unlimited"),
      team: t("limits.unlimited"),
    },
    {
      type: "feature",
      feature: t("limits.vennDiagrams"),
      tooltip: t("limits.vennDiagramsTooltip"),
      free: t("limits.free3dayVisual"),
      pro: t("limits.unlimited"),
      team: t("limits.unlimited"),
    },

    // ── Support ──
    { type: "category", label: t("limits.support") },
    {
      type: "feature",
      feature: t("limits.prioritySupport"),
      free: false,
      pro: false,
      team: true,
    },

    // ── Collaboration ──
    { type: "category", label: t("limits.collaboration") },
    {
      type: "feature",
      feature: t("limits.teamSharedSpaces"),
      tooltip: t("limits.teamSharedSpacesTooltip"),
      free: false,
      pro: t("limits.teamsAddon"),
      team: true,
    },
  ];

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="w-[220px] text-xs font-medium uppercase text-muted-foreground">
            {t("limits.featureHeader")}
          </TableHead>
          <TableHead className="text-center">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Free
            </span>
          </TableHead>
          <TableHead className="text-center">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Pro
            </span>
          </TableHead>
          <TableHead className="text-center">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Team
            </span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {FEATURES.map((row, i) => {
          if (row.type === "category") {
            return (
              <TableRow key={i} className="hover:bg-transparent">
                <TableCell
                  colSpan={4}
                  className="bg-muted/30 py-2 text-xs font-medium text-muted-foreground sm:text-sm"
                >
                  {row.label}
                </TableCell>
              </TableRow>
            );
          }

          return (
            <TableRow key={i} className="hover:bg-transparent">
              <TableCell className="py-2 pl-4 text-xs font-medium sm:text-sm">
                <FeatureLabel feature={row.feature} tooltip={row.tooltip} />
              </TableCell>
              <TableCell className="py-2 text-center text-xs sm:text-sm">
                <CellValue value={row.free} />
              </TableCell>
              <TableCell className="py-2 text-center text-xs sm:text-sm">
                <CellValue value={row.pro} />
              </TableCell>
              <TableCell className="py-2 text-center text-xs sm:text-sm">
                <CellValue value={row.team} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
